import React, { useState, FC, useEffect } from "react"
import Bar from "../../../components/Bar"
import { connect } from "react-redux"
import { IStoreState } from "../../../reducers"
import { ArrayState } from "../../../reducers/types"
import { SET_ARRAY } from "../../../reducers/array"
import InsertionSortTracker, { IInsertionSortTrace } from "./InsertionSortTracker"
import Chart from "../../../components/Chart"

interface IInsertionSortProps {
    initialData: number[]
    startSort: boolean
    isSorted: boolean
    onComplete: () => void
    setArray: (array: number[]) => void
}

const insertionSortAlgo = (data: number[]): InsertionSortTracker => {
    const arr = [...data]
    const tracker = new InsertionSortTracker([...arr])

    for (let i = 1; i < arr.length; i++) {
        let j = i - 1
        let temp = arr[i]
        tracker.traceCompare([...arr], [i, j])
        while (j >= 0 && arr[j] > temp) {
            arr[j + 1] = arr[j]
            arr[j] = temp
            j--
            tracker.traceCompare([...arr], [j + 1, j])
        }
        tracker.traceSwap([...arr], [j, j + 1])
        arr[j + 1] = temp
    }

    tracker.trackSorted([...arr])
    return tracker
}

const InsertionSort: FC<IInsertionSortProps> = ({
    initialData,
    startSort,
    isSorted,
    onComplete,
    setArray,
}: IInsertionSortProps) => {
    const [compare, setCompare] = useState<[number, number]>([-1, -1])
    const [swapped, setSwapped] = useState<[number, number]>([-1, -1])

    const data = initialData
    const numWidth = 98 / data.length
    const width = `${numWidth}%`
    const color = numWidth < 20 ? "white" : "transparent"

    function handleDispatch({ traces, speed }: { traces: IInsertionSortTrace[]; speed: number }) {
        if (!traces.length) {
            onComplete()
            return
        }

        const trace = traces.shift()
        if (!trace) {
            onComplete()
            return
        }
        setArray(trace.data)
        setCompare(trace.comparedElements)
        setSwapped(trace.swappedElements)

        setTimeout(() => {
            handleDispatch({ traces, speed })
        }, speed)
    }

    function onClickHandler() {
        const tracker = insertionSortAlgo(data)

        handleDispatch({ traces: tracker.traces.splice(0), speed: 0 })
    }

    useEffect(() => {
        if (startSort) {
            onClickHandler()
        }
        // eslint-disable-next-line
    }, [startSort])

    return (
        <Chart>
            {data.map((number: number, index: number) => {
                const backgroundColor = swapped.includes(index)
                    ? "rgba(219, 57, 57, 0.8)"
                    : compare.includes(index)
                    ? "rgba(78, 216, 96, 0.8)"
                    : isSorted
                    ? "#22AF4B"
                    : "rgba(66, 134, 244, 0.8)"

                return (
                    <Bar
                        key={index}
                        height={`${(number / data.length) * 100}%`}
                        width={width}
                        bgColor={backgroundColor}
                        color={color}
                    />
                )
            })}
        </Chart>
    )
}

const mapStateToProps = (state: IStoreState) => {
    return {
        isSorted: state.arrayState === ArrayState.Sorted,
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        setArray: (array: number[]) => dispatch({ type: SET_ARRAY, payload: array }),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(InsertionSort)
